import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { okaidiaInit } from '@uiw/codemirror-theme-okaidia';

import { FormField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import { LocalTime } from '~/components/common/LocalTime';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  ConfirmModal,
  FormActions,
  LoadError,
  SkeletonFields,
  StatusStrip,
  type StatusTone,
  Tabs,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlTab } from '~/hooks/useUrlTab';
import { routes } from '~/app/routes';
import { isNotFound } from '~/lib/apiError';
import { feedConfigPayload, feedConfigSchema } from '~/lib/feedConfig';
import { plural } from '~/lib/format';
import { createDefaults, type MlsConfigDraft, toDraft } from '~/lib/mlsConfigForm';
import { themeColor } from '~/lib/theme-colors';
import { queryErrorMessage } from '~/modules/query';
import {
  useGetMlsConfigQuery,
  useGetMlsQuery,
  useGetViewConfigQuery,
  useTrestleReportMutation,
  useUpdateMlsMutation,
} from '~/modules/query/mls';
import type { MlsRow } from '~/components/Constants/mlsConstants';
import { feedId, feedWrite, mlsKeyFor } from '~/components/pages/mls/components/feedActions';
import MlsConfigTab from '~/components/pages/mls/components/MlsConfigTab';
import MlsDeleteConfirm from '~/components/pages/mls/components/mlsDeleteConfirm';
import MlsCustomerTable, {
  feedCustomers,
} from '~/components/pages/mls/components/viewCustomersTable';
import { FeedDuplicateModal, FeedLogo } from '~/components/pages/mls/components/feedSections';

/** How many of the feed's two platforms (Office, Social) it is connected to. */
const connectionChip = (feed: MlsRow): { label: string; tone: StatusTone } => {
  const connected = [feed.targets?.ClientBAY, feed.targets?.SocialBAY].filter((t) => t?.key).length;
  if (connected === 2) return { label: 'Connected', tone: 'success' };
  return connected === 1
    ? { label: 'Partly connected', tone: 'warning' }
    : { label: 'Not connected', tone: 'error' };
};

const SaveError = ({ message }: { message: string | null }) =>
  message ? (
    <Alert variant="error">
      <p className="text-sm text-gray-800 dark:text-white/90">{message}</p>
    </Alert>
  ) : null;

const TABS = ['overview', 'configuration', 'customers'] as const;

const TAB_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'customers', label: 'Customers' },
];

const Panel = ({ tab, children }: { tab: (typeof TABS)[number]; children: React.ReactNode }) => (
  <section
    role="tabpanel"
    id={`feed-detail-panel-${tab}`}
    aria-labelledby={`feed-detail-tab-${tab}`}
  >
    {children}
  </section>
);

/** One feed as a tabbed record: an overview (logo and MLS config) and the Mongo configuration (each with its own Save), and customers (DESIGN.md §1.4, §1.6). */
export default function FeedDetailPage() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const list = useGetMlsQuery();
  const feed = (list.data as MlsRow[] | undefined)?.find((row) => feedId(row) === id);

  const config = useGetViewConfigQuery(id, { skip: !feed });
  const mongoData = config.currentData?.mongoData ?? config.currentData;
  const { draft, patch, dirty, reset, markSaved } = useRecordDraft(
    mongoData === undefined ? undefined : JSON.stringify(mongoData, null, 2),
    { id, toDraft: (text: string) => ({ config: text }) },
  );
  const [tab, setTab] = useUrlTab(TABS, 'overview');

  // The realistiq MLS row of this feed. Its draft lives here so it survives a tab switch.
  const mlsid = String(feed?.mlsid ?? '').trim();
  const mlsQuery = useGetMlsConfigQuery(mlsid, { skip: !mlsid });
  const mlsRow = mlsQuery.isError ? undefined : mlsQuery.currentData;
  const mlsAbsent = isNotFound(mlsQuery.error);
  const mlsSource = useMemo(
    () => (mlsRow ? toDraft(mlsRow) : mlsAbsent ? toDraft(createDefaults(mlsid)) : undefined),
    [mlsRow, mlsAbsent, mlsid],
  );
  const mls = useRecordDraft<MlsConfigDraft, MlsConfigDraft>(mlsSource, {
    id: mlsid,
    toDraft: (source) => source,
  });
  const guard = useUnsavedGuard(dirty || mls.dirty);
  const v = useFormValidation(feedConfigSchema, draft, () => 'feed-config');
  const editor = useRef<ReactCodeMirrorRef>(null);
  // A library colour cannot read classes, so the caret comes from the token (DESIGN.md §3.6).
  const theme = useMemo(
    () => okaidiaInit({ settings: { caret: themeColor('gray-300'), fontFamily: 'monospace' } }),
    [],
  );

  const [updateMls] = useUpdateMlsMutation();
  const [trestleReport] = useTrestleReportMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmTrestle, setConfirmTrestle] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const save = useRecordSave<{ config: string }>({
    save: (d) =>
      feedWrite(
        updateMls({
          mlsid: mlsKeyFor(feed),
          data: { mongoData: feedConfigPayload(d.config) },
        }).unwrap(),
      ),
    success: 'Configuration updated',
    failure: 'The configuration could not be saved. Try again.',
  });
  const report = useRecordSave<void>({
    save: () => feedWrite(trestleReport(mlsKeyFor(feed)).unwrap()),
    success: 'Trestle report requested',
    failure: 'The Trestle report could not be generated. Try again.',
  });

  const title = feed?.name ?? feed?._id ?? 'Feed';
  const loadFailed = !feed && list.isError;

  const actions = feed
    ? [
        {
          label: 'Duplicate feed',
          kind: 'secondary' as const,
          onClick: () => setDuplicating(true),
          testId: 'feed-duplicate-button',
        },
        {
          label: 'Delete feed',
          kind: 'danger' as const,
          onClick: () => setConfirmDelete(true),
          testId: 'feed-delete-button',
        },
        ...(feed.vendor === 'trestle'
          ? [
              {
                label: 'Generate Trestle report',
                kind: 'secondary' as const,
                onClick: () => setConfirmTrestle(true),
                testId: 'feed-trestle-button',
              },
            ]
          : []),
      ]
    : undefined;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    if (!v.submit()) {
      editor.current?.view?.focus();
      return;
    }
    if (await save.submit(draft)) markSaved(draft);
  };

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[{ label: 'Feeds', href: routes.feeds }, { label: title }]}
      actions={actions}
      loading={!feed && list.isLoading}
      notFound={!feed && !list.isLoading && !list.isError}
      notFoundTitle="Feed not found"
      backTo={routes.feeds}
      backLabel="Back to feeds"
      error={loadFailed ? { what: 'the feed', message: queryErrorMessage(list.error) } : null}
      onRetry={() => list.refetch()}
      guard={guard}
      testId="feed"
    >
      {feed && (
        <>
          <StatusStrip
            chip={connectionChip(feed)}
            id={feed._id}
            time={
              <>
                Last download <LocalTime value={feed.dtLastDownload} />
              </>
            }
            facts={[plural(feedCustomers(feed).length, 'customer')]}
            testId="feed-status"
          />
          <SaveError message={report.error} />
          <Tabs
            variant="underline"
            ariaLabel="Feed sections"
            idPrefix="feed-detail"
            activeId={tab}
            onChange={(id) => setTab(id as (typeof TABS)[number])}
            tabs={TAB_ITEMS}
          />

          {tab === 'overview' && (
            <Panel tab="overview">
              <MlsConfigTab
                logo={<FeedLogo feed={feed} />}
                mlsid={mlsid}
                config={{
                  row: mlsRow,
                  absent: mlsAbsent,
                  error: mlsQuery.error,
                  refetch: () => mlsQuery.refetch(),
                }}
                form={mls}
              />
            </Panel>
          )}

          {tab === 'configuration' && (
            <Panel tab="configuration">
              <Card>
                <CardBody className="space-y-6">
                  <CardSection
                    title="Configuration"
                    description="The feed's Mongo configuration, as JSON."
                  >
                    <SaveError message={save.error} />
                    {draft ? (
                      <form id="feed-config-form" noValidate onSubmit={submit}>
                        <FormField id="feed-config" label="Configuration" error={v.errors.config}>
                          <CodeMirror
                            id="feed-config"
                            ref={editor}
                            theme={theme}
                            value={draft.config}
                            minHeight="200px"
                            onChange={(text) => patch({ config: text })}
                            onBlur={() => v.touch('config')}
                            extensions={[javascript({ jsx: true })]}
                          />
                        </FormField>
                      </form>
                    ) : config.error ? (
                      <LoadError
                        what="the configuration"
                        message={queryErrorMessage(config.error) ?? 'Try again in a moment.'}
                        onRetry={config.refetch}
                      />
                    ) : (
                      <SkeletonFields fields={3} />
                    )}
                  </CardSection>
                </CardBody>
                <CardFooter>
                  <FormActions
                    form="feed-config-form"
                    onDiscard={() => {
                      reset();
                      v.reset();
                      save.clearError();
                    }}
                    busy={save.busy}
                    disabled={!dirty}
                    disabledReason="No changes to save"
                    saveTestId="feed-save"
                    discardTestId="feed-discard"
                  />
                </CardFooter>
              </Card>
            </Panel>
          )}

          {tab === 'customers' && (
            <Panel tab="customers">
              <Card>
                <CardBody>
                  <MlsCustomerTable rows={feedCustomers(feed)} />
                </CardBody>
              </Card>
            </Panel>
          )}

          <FeedDuplicateModal
            feed={feed}
            isOpen={duplicating}
            onClose={() => setDuplicating(false)}
          />
          <MlsDeleteConfirm
            feed={confirmDelete ? feed : null}
            onClose={() => setConfirmDelete(false)}
            onDeleted={() => {
              guard.release();
              navigate(routes.feeds);
            }}
          />
          <ConfirmModal
            isOpen={confirmTrestle}
            title="Generate Trestle report"
            message={
              <>
                Generate the Trestle report for <strong>{title}</strong>?
              </>
            }
            confirmLabel="Generate report"
            testId="feed-trestle-confirm"
            onClose={() => setConfirmTrestle(false)}
            onConfirm={async () => {
              await report.submit();
              setConfirmTrestle(false);
            }}
          />
        </>
      )}
    </RecordPageShell>
  );
}
